/**
 * useReactiveNode - Subscribe to a single node in GraphStore
 *
 * Returns a reactive reference that updates when the node changes in GraphStore
 * (e.g., via DAG patches or save operations).
 *
 * This is the single-node equivalent of useNodes() for relationships.
 */

import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { graphStore, graphStoreJotaiStore, type NodeId } from '@sourcepilot/framework/graph-store';
import type { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';

/**
 * Subscribe to a node by ID and get reactive updates
 *
 * @param nodeId - The ID of the node to subscribe to
 * @returns The node instance, or null if not found
 *
 * @example
 * ```tsx
 * function ProductView({ productId }: { productId: string }) {
 *   const product = useReactiveNode<PDMProduct>(productId);
 *
 *   if (!product) return <div>Loading...</div>;
 *
 *   // Component re-renders when product changes via DAG patches
 *   return <div>{product.name} - {product.development_stage}</div>;
 * }
 * ```
 */
export function useReactiveNode<T extends NeoNodeBase<any, any>>(
  nodeId: NodeId | null | undefined
): T | null {
  // Get the atom for this node
  const nodeAtom = useMemo(
    () => nodeId ? graphStore.getNodeAtom(nodeId) : null,
    [nodeId]
  );

  // Subscribe to the atom - re-renders when node changes
  // IMPORTANT: Use graphStoreJotaiStore instead of default React store
  const node = useAtomValue(
    nodeAtom ?? graphStore.getNodeAtom('__null__'),
    { store: graphStoreJotaiStore }
  );

  return node as T | null;
}
